import { useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  ApiKeysService,
  type ApiKeyDetail,
  type RevokeApiKeyRequest,
  type RevokeApiKeyResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface ApiKeyRevokeDialogProps {
  apiKey: ApiKeyDetail | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Revoke — irreversible. The user has to type the key name back
 * into the confirmation field to enable the destructive button —
 * Anthropic Console pattern. Reason is optional but lands in the
 * audit chain.
 */
export function ApiKeyRevokeDialog({
  apiKey,
  onClose,
  onSuccess,
}: ApiKeyRevokeDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [confirmName, setConfirmName] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<
    RevokeApiKeyRequest,
    RevokeApiKeyResponse
  >((body) => ApiKeysService.revoke(apiKey!.id, body), {
    onSuccess: () => {
      setReason("");
      setConfirmName("");
      onSuccess();
    },
  });

  if (!apiKey) return null;

  const matches = confirmName.trim() === apiKey.name;

  const handleClose = () => {
    reset();
    setReason("");
    setConfirmName("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matches) return;
    const body: RevokeApiKeyRequest = {};
    const trimmed = reason.trim();
    if (trimmed) body.reason = trimmed;
    await mutate(body);
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("apiKeys.revokeDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("apiKeys.revokeDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Alert variant="danger">
            <div className="flex items-start gap-2 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                {t("apiKeys.revokeDialog.confirmPrompt")}
                <strong className="font-mono">{apiKey.name}</strong>
              </div>
            </div>
          </Alert>

          <FormField
            label={t("apiKeys.revokeDialog.confirmLabel")}
            value={confirmName}
            onChange={setConfirmName}
            placeholder={apiKey.name}
            error={
              confirmName.length > 0 && !matches
                ? t("apiKeys.revokeDialog.confirmMismatch")
                : undefined
            }
            required
          />
          <FormField
            label={t("apiKeys.revokeDialog.reasonLabel")}
            value={reason}
            onChange={setReason}
            placeholder={t("apiKeys.revokeDialog.reasonPlaceholder")}
            maxLength={500}
          />
          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("apiKeys.revokeDialog.cancel")}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={isLoading || !matches}
          >
            {isLoading
              ? t("apiKeys.revokeDialog.submitting")
              : t("apiKeys.revokeDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
